import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "flowbite-react";
import { FieldLabel } from "../../../atoms/FieldLabel";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomPhoneNumber } from "../../../atoms/CustomPhoneNumber";
import {
  CreateLeadInput,
  LEAD_FIELD_LIMITS,
} from "../../../../types/LeadTypes";
import { leadIdentitySchema } from "./leadSchema";

interface Props {
  show: boolean;
  onClose: () => void;
  onSubmit: (input: CreateLeadInput) => void;
  isSubmitting?: boolean;
}

interface QuickForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

const emptyForm: QuickForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
};

const schema = Joi.object<QuickForm>(leadIdentitySchema);

const AddLeadModal: React.FC<Props> = ({
  show,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setFocus,
    formState: { errors },
  } = useForm<QuickForm>({
    mode: "onChange",
    resolver: joiResolver(schema),
    defaultValues: emptyForm,
  });

  useEffect(() => {
    if (show) {
      reset(emptyForm);
      const id = setTimeout(() => setFocus("first_name"), 50);
      return () => clearTimeout(id);
    }
  }, [show]);

  const values = watch();
  const hasSomething = [
    values.first_name,
    values.last_name,
    values.email,
    values.phone,
  ].some((v) => (v || "").trim());

  const submit = (data: QuickForm) => {
    if (isSubmitting || !hasSomething) return;
    onSubmit({
      first_name: data.first_name?.trim(),
      last_name: data.last_name?.trim(),
      email: data.email?.trim(),
      phone: data.phone?.trim(),
    });
  };

  const field = (
    name: keyof QuickForm,
    label: string,
    max: number,
    type = "text"
  ) => (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <TextInput
        type={type}
        maxLength={max}
        {...register(name)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit(submit)();
          }
        }}
        className="w-full"
      />
    </div>
  );

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="md"
      dismissible
      title={t("leads.addLeadModal.title")}
      subtitle={t("leads.addLeadModal.text")}
      footer={
        <>
          <CustomButton
            onClick={onClose}
            title={t("common.cancel") as string}
            variant="secondary"
          />
          <CustomButton
            onClick={handleSubmit(submit)}
            variant="primary"
            disabled={
              isSubmitting ||
              !hasSomething ||
              !permissions.can(Cap.LEADS, Level.WRITE)
            }
            isLoading={isSubmitting}
            title={t("leads.addLead") as string}
          />
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {field(
            "first_name",
            t("leads.addLeadModal.firstName") as string,
            LEAD_FIELD_LIMITS.first_name.max
          )}
          {field(
            "last_name",
            t("leads.addLeadModal.lastName") as string,
            LEAD_FIELD_LIMITS.last_name.max
          )}
        </div>
        {field(
          "email",
          t("leads.addLeadModal.email") as string,
          LEAD_FIELD_LIMITS.email.max,
          "email"
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("leads.addLeadModal.phone")}
          </label>
          <CustomPhoneNumber
            name="phone"
            control={control}
            error={errors.phone}
            helperText={errors.phone?.message as string}
            labelVariant="none"
          />
        </div>
      </div>
    </ModalShell>
  );
};

export default AddLeadModal;
